import { useCallback, useEffect, useRef, useState } from "react";
import emitter from "../../Utils/events/eventBus";
import endpoints from "../../Helpers/Endpoints";
import { apiDeleteRequest, apiGetRequest, apiPostRequest } from "../../Helpers/Requests";
import { readAgentStream } from "./streamReader";
import { trackAgent } from "./analytics";

// One chat with the assistant (COMY1-390). Each turn sends the conversation's
// text, and the server replays nothing but that text to the model. The
// conversation id is a new uuid per chat; the server keeps it to the person and
// role view it started in.
//
// Writes (COMY1-391) come back as actions under the reply: a Confirm card the
// person answers with Confirm / Edit / Cancel, or a done write with a receipt
// and, for a minute, Undo. The decision goes to the server by the action's id
// only; whatever the server answers replaces the card.
//
// Saved chats and files (COMY1-392): the server saves every message, so the
// open chat comes back after a reload (its id is remembered in this browser)
// and older ones are listed in the history. A restored card shows its state
// as the server has it now — one left pending past its 10 minutes is expired.
// Files picked with the paperclip are uploaded at once and sent with the next
// message by id only.

export const AGENT_MESSAGE_MAX_CHARS = 2000;
const HISTORY_SENT = 40;
const HANDOFF_TOOL = "handoff_open_screen";
const COMMUNITY_SWITCH_TOOL = "community_switch";
const PLATFORM_TERMS_REQUIRED = "PLATFORM_TERMS_REQUIRED";

/** What the chat takes: photos (JPEG, PNG, WebP) and PDFs up to 25 MB, a few per message. The server checks again. */
export const AGENT_ATTACHMENT_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
export const AGENT_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const AGENT_ATTACHMENTS_PER_MESSAGE = 6;

const storageKey = (key) => `keilot-agent-chat:${key}`;
const remembered = (key) => {
  try {
    return window.localStorage.getItem(storageKey(key));
  } catch {
    return null;
  }
};
const remember = (key, conversationId) => {
  try {
    if (conversationId) window.localStorage.setItem(storageKey(key), conversationId);
    else window.localStorage.removeItem(storageKey(key));
  } catch {
    // private mode: the chat just won't come back after a reload
  }
};

const newId = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
        (
          c ^
          (window.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16),
      );

/**
 * What a tool result adds under the assistant's reply: a lookup chip for a
 * read, a button for a handoff, a card for a proposed write, a receipt (with
 * Undo) for a write that ran. A refused write may come with the app's screen
 * for it (no bank details → Business Settings) as a button.
 */
function extrasOf(toolName, output) {
  if (!output?.ok) {
    if (output?.code === PLATFORM_TERMS_REQUIRED) emitter.emit("platform-terms-required");
    return output?.handoff ? { button: output.handoff } : {};
  }
  if (output.status === "awaiting_confirmation") {
    trackAgent("write_proposed", { tool: toolName });
    return {
      action: { id: output.actionId, toolName, status: "proposed", card: output.card, ...localDeadlines(output) },
    };
  }
  if (output.actionId) {
    // an auto write that already ran (the language): its receipt, with Undo — except a community switch,
    // after which the app reloads in that community
    const deadlines = localDeadlines(output);
    return {
      action: { id: output.actionId, toolName, status: "executed", receipt: output.receipt, ...deadlines, ...(toolName === COMMUNITY_SWITCH_TOOL ? { undoUntil: null } : {}) },
    };
  }
  if (toolName === HANDOFF_TOOL && output.result?.link) {
    return { button: { label: output.receipt, link: output.result.link } };
  }
  return output.receipt ? { chip: output.receipt } : {};
}

/**
 * The server's windows as deadlines on this browser's clock: it sends the seconds
 * left (measured on the database's clock), and the countdown starts when the
 * answer arrives — the two clocks can be minutes apart.
 */
const localDeadlines = (view) => ({
  expiresAt: view.expiresInSeconds != null ? new Date(Date.now() + view.expiresInSeconds * 1000).toISOString() : view.expiresAt,
  undoUntil: view.undoSeconds != null ? new Date(Date.now() + view.undoSeconds * 1000).toISOString() : view.undoUntil,
});

/** Analytics name of what a decision did to a card. */
const OUTCOME = { executed: "write_confirmed", declined: "write_declined", expired: "write_expired", undone: "write_undone", failed: "write_failed" };

/** A saved message as the chat shows it: a reply's receipts, cards (as the server has them now) and buttons. */
function restoredMessage(m, actions) {
  if (m.role === "user") return { id: m.id, role: "user", text: m.text, attachments: m.attachments ?? [] };
  const reply = { id: m.id, role: "assistant", text: m.text, chips: [], buttons: [], actions: [], feedback: null };
  for (const { toolName, output } of m.tools ?? []) {
    const view = output?.actionId ? actions?.[output.actionId] : null;
    // a community switch offers no Undo (the app reloads there), restored or live
    if (view) reply.actions.push({ ...view, ...localDeadlines(view), ...(view.toolName === COMMUNITY_SWITCH_TOOL || toolName === COMMUNITY_SWITCH_TOOL ? { undoUntil: null } : {}) });
    else if (output?.ok === false && output.handoff) reply.buttons.push(output.handoff);
    else if (toolName === HANDOFF_TOOL && output?.result?.link) reply.buttons.push({ label: output.receipt, link: output.result.link });
    else if (output?.receipt) reply.chips.push(output.receipt);
  }
  return reply;
}

export default function useAgentChat({ locale, resetKey }) {
  const [conversationId, setConversationId] = useState(newId);
  const [messages, setMessages] = useState([]);
  // idle | waiting (sent, nothing back yet) | streaming
  const [status, setStatus] = useState("idle");
  // null | { kind: "unavailable" | "notEnabled" | "invalid", message }
  const [error, setError] = useState(null);
  // files picked for the next message: { localId, id?, name, kind, previewUrl, uploading, error? }
  const [pending, setPending] = useState([]);
  // a saved chat being loaded
  const [restoring, setRestoring] = useState(false);
  const abortRef = useRef(null);
  // pending as of now, for checks between renders (several files picked at once)
  const pendingRef = useRef([]);
  // the latest openConversation: an older load that lands late is dropped
  const openSeq = useRef(0);
  // a manager's chat switched community (S5): the app reloads there, as the sidebar's switch does
  const [communitySwitched, setCommunitySwitched] = useState(false);

  const updatePending = useCallback((update) => {
    const next = typeof update === "function" ? update(pendingRef.current) : update;
    pendingRef.current = next;
    setPending(next);
  }, []);

  /** Drops the picked files that were never sent, with their local previews. */
  const dropPending = useCallback(() => {
    pendingRef.current.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
    updatePending([]);
  }, [updatePending]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    openSeq.current += 1;
    setRestoring(false);
    setConversationId(newId());
    setMessages([]);
    dropPending();
    setError(null);
    setStatus("idle");
    remember(resetKey, null);
  }, [dropPending, resetKey]);

  /** Opens a saved chat: its messages, and its cards as they are now. */
  const openConversation = useCallback(
    async (id) => {
      abortRef.current?.abort();
      const seq = ++openSeq.current;
      setRestoring(true);
      setError(null);
      dropPending();
      try {
        const { data } = await apiGetRequest(endpoints.agentConversation(id, locale));
        if (seq !== openSeq.current) return false;
        setConversationId(id);
        setMessages((data.messages ?? []).map((m) => restoredMessage(m, data.actions)));
        setStatus("idle");
        remember(resetKey, id);
        return true;
      } catch {
        // gone (deleted, or from another role view): start a new chat
        if (seq === openSeq.current && remembered(resetKey) === id) remember(resetKey, null);
        return false;
      } finally {
        if (seq === openSeq.current) setRestoring(false);
      }
    },
    [dropPending, locale, resetKey],
  );

  // Switching the role view is a new conversation (the server refuses the old one);
  // on load, the chat this browser had open comes back.
  useEffect(() => {
    abortRef.current?.abort();
    openSeq.current += 1;
    setRestoring(false);
    setConversationId(newId());
    setMessages([]);
    dropPending();
    setError(null);
    setStatus("idle");
    const last = remembered(resetKey);
    if (last) openConversation(last);
    // only when the person or role view changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const updateLast = (update) =>
    setMessages((current) => {
      const next = [...current];
      next[next.length - 1] = update(next[next.length - 1]);
      return next;
    });

  /** Uploads a picked file at once; it goes with the next message. Type and size are checked here and again on the server. */
  const attach = useCallback(
    async (file) => {
      const localId = newId();
      const kind = file.type === "application/pdf" ? "pdf" : "image";
      if (!AGENT_ATTACHMENT_TYPES.includes(file.type)) return { error: "type" };
      if (file.size > AGENT_ATTACHMENT_MAX_BYTES) return { error: "size" };
      if (pendingRef.current.length >= AGENT_ATTACHMENTS_PER_MESSAGE) return { error: "count" };
      const previewUrl = kind === "image" ? URL.createObjectURL(file) : null;
      updatePending((list) => [...list, { localId, name: file.name, kind, previewUrl, uploading: true }]);
      const form = new FormData();
      form.append("conversationId", conversationId);
      form.append("locale", locale);
      form.append("file", file);
      try {
        const { data } = await apiPostRequest(endpoints.agentAttachments, form, { "Content-Type": "multipart/form-data" });
        updatePending((list) => list.map((a) => (a.localId === localId ? { ...a, id: data.id, kind: data.kind, uploading: false } : a)));
        remember(resetKey, conversationId);
        trackAgent("file_attached", { kind: data.kind });
        return {};
      } catch (err) {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        updatePending((list) => list.filter((a) => a.localId !== localId));
        const code = err?.response?.data?.code;
        return { error: code === "FILE_TOO_LARGE" ? "size" : code === "UNSUPPORTED_FILE" ? "type" : code === "TOO_MANY_FILES" ? "count" : "upload" };
      }
    },
    [conversationId, locale, resetKey, updatePending],
  );

  const detach = useCallback(
    (attachment) => {
      if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
      updatePending((list) => list.filter((a) => a.localId !== attachment.localId));
    },
    [updatePending],
  );

  const send = useCallback(
    async (rawText) => {
      const text = rawText.trim().slice(0, AGENT_MESSAGE_MAX_CHARS);
      if (pending.some((a) => a.uploading)) return;
      const files = pending.filter((a) => a.id).map(({ id, name, kind, previewUrl }) => ({ id, name, kind, previewUrl }));
      if ((!text && !files.length) || status !== "idle") return;
      setError(null);
      // the previews go with the message, so they aren't revoked here
      updatePending([]);
      remember(resetKey, conversationId);
      const history = [...messages, { id: newId(), role: "user", text, attachments: files }];
      // pendingTools: the lookups the model is waiting on right now, for the "Looking it up…" line
      const reply = { id: newId(), role: "assistant", text: "", chips: [], buttons: [], actions: [], feedback: null, pendingTools: [] };
      setMessages([...history, reply]);
      setStatus("waiting");
      const turn = history.filter((m) => m.role === "user").length;
      if (turn === 1) trackAgent("conversation_started");
      trackAgent("turn_sent", { turn });

      const controller = new AbortController();
      abortRef.current = controller;
      const toolNames = {};
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}${endpoints.agentChat}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
          signal: controller.signal,
          body: JSON.stringify({
            id: conversationId,
            locale,
            messages: history
              .filter((m) => m.text || m.attachments?.length)
              .slice(-HISTORY_SENT)
              .map((m) => ({
                id: m.id,
                role: m.role,
                parts: [
                  ...(m.text ? [{ type: "text", text: m.text }] : []),
                  // files by id only: the server looks each one up
                  ...(m.attachments ?? []).map((a) => ({ type: "data-attachment", data: { id: a.id } })),
                ],
              })),
          }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const kind =
            response.status === 403 && body.code === "AGENT_NOT_ENABLED"
              ? "notEnabled"
              : response.status === 400
                ? "invalid"
                : "unavailable";
          setError({ kind, message: body.message });
          setMessages(history);
          return;
        }

        let failed = null;
        await readAgentStream(response, (event) => {
          switch (event.type) {
            case "text-start":
              // each model step is a new text part: a paragraph break, not glued on
              updateLast((m) => (m.text && !m.text.endsWith("\n") ? { ...m, text: `${m.text}\n\n` } : m));
              break;
            case "text-delta":
              setStatus("streaming");
              updateLast((m) => ({ ...m, text: m.text + event.delta }));
              break;
            case "tool-input-available":
            case "tool-input-start":
              toolNames[event.toolCallId] = event.toolName;
              // the model stopped writing to look something up: back to the thinking line until its next word
              setStatus("waiting");
              updateLast((m) => ({
                ...m,
                pendingTools: (m.pendingTools ?? []).includes(event.toolCallId)
                  ? m.pendingTools
                  : [...(m.pendingTools ?? []), event.toolCallId],
              }));
              break;
            case "tool-output-available":
            case "tool-output-error": {
              if (event.type === "tool-output-available") {
                trackAgent("tool_used", { tool: toolNames[event.toolCallId] ?? "unknown", ok: !!event.output?.ok });
              }
              const { chip, button, action } =
                event.type === "tool-output-available" ? extrasOf(toolNames[event.toolCallId], event.output) : {};
              if (action?.toolName === COMMUNITY_SWITCH_TOOL && action.status === "executed") setCommunitySwitched(true);
              updateLast((m) => ({
                ...m,
                chips: chip ? [...m.chips, chip] : m.chips,
                buttons: button ? [...m.buttons, button] : m.buttons,
                actions: action ? [...m.actions, action] : m.actions,
                pendingTools: (m.pendingTools ?? []).filter((id) => id !== event.toolCallId),
              }));
              break;
            }
            case "error":
              failed = event.errorText;
              break;
            default:
              break;
          }
        });
        if (failed) {
          setError({ kind: "unavailable", message: failed });
          // a reply that never started isn't kept; one cut off keeps what came
          setMessages((current) => {
            const last = current[current.length - 1];
            return last?.role === "assistant" && !last.text && !last.chips.length && !last.actions.length ? current.slice(0, -1) : current;
          });
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Agent chat failed:", err);
          setError({ kind: "unavailable" });
        }
        setMessages((current) => {
          const last = current[current.length - 1];
          return last?.role === "assistant" && !last.text ? current.slice(0, -1) : current;
        });
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setStatus("idle");
      }
    },
    [conversationId, locale, messages, pending, resetKey, status, updatePending],
  );

  /** Saved chats of this role view, newest first. */
  const loadConversations = useCallback(async () => {
    const { data } = await apiGetRequest(endpoints.agentConversations(locale));
    return data?.conversations ?? [];
  }, [locale]);

  /** Deletes one saved chat, or every one (id null). The record of what was done stays on the server. */
  const deleteConversations = useCallback(
    async (id) => {
      await apiDeleteRequest(id ? endpoints.agentConversation(id, locale) : endpoints.agentConversations(locale));
      trackAgent("history_deleted", { scope: id ? "one" : "all" });
      if (!id || id === conversationId) reset();
    },
    [conversationId, locale, reset],
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  /** Puts what the server says about an action on its card, wherever it is shown. */
  const patchAction = useCallback((actionId, patch) => {
    setMessages((current) =>
      current.map((m) =>
        m.actions?.some((a) => a.id === actionId)
          ? { ...m, actions: m.actions.map((a) => (a.id === actionId ? { ...a, ...patch } : a)) }
          : m,
      ),
    );
  }, []);

  // actions being answered right now: a second tap waits for the first
  const deciding = useRef(new Set());

  /**
   * Confirm / Cancel / Edit (decline with reason edit) / Undo on a card. The
   * server checks owner, expiry and the current state, and runs the write once.
   */
  const decide = useCallback(
    async (action, decision, reason) => {
      if (deciding.current.has(action.id)) return;
      deciding.current.add(action.id);
      patchAction(action.id, { busy: decision });
      try {
        const { data } = await apiPostRequest(endpoints.agentDecision(action.id, decision), { locale, reason });
        const view = data.action ?? {};
        patchAction(action.id, { ...view, ...localDeadlines(view), message: data.message ?? null, busy: null });
        const event = data.changed && view.status === "proposed" ? "write_refreshed" : OUTCOME[view.status];
        if (event) trackAgent(event, { tool: action.toolName, ...(decision === "decline" ? { reason: reason ?? "cancel" } : {}) });
      } catch (err) {
        const body = err?.response?.data ?? {};
        // the terms window is opened by the request helper (PLATFORM_TERMS_REQUIRED)
        patchAction(action.id, { ...(body.action ?? {}), ...(body.action ? localDeadlines(body.action) : {}), message: body.message ?? null, busy: null });
        if (body.code === "ACTION_EXPIRED") trackAgent("write_expired", { tool: action.toolName });
        else if (body.code === "UNDO_FAILED" || body.code === "UNDO_EXPIRED") trackAgent("write_failed", { tool: action.toolName, step: "undo" });
      } finally {
        deciding.current.delete(action.id);
      }
    },
    [locale, patchAction],
  );

  /** A card whose 10 minutes ran out on screen: shown as expired (the server agrees on the next tap). */
  const expire = useCallback(
    (action) => {
      patchAction(action.id, { status: "expired" });
      trackAgent("write_expired", { tool: action.toolName });
    },
    [patchAction],
  );

  /** The "What I did" feed: every write of this chat, newest first. */
  const loadFeed = useCallback(async () => {
    const { data } = await apiGetRequest(endpoints.agentActions(conversationId, locale));
    return data?.actions ?? [];
  }, [conversationId, locale]);

  // Thumbs up / down on a reply: a pilot metric only (analytics), changeable until the next one.
  const rate = useCallback(
    (messageId, value) => {
      const index = messages.findIndex((m) => m.id === messageId);
      if (index === -1 || messages[index].feedback === value) return;
      trackAgent("feedback", { value, turn: messages.slice(0, index).filter((m) => m.role === "user").length });
      setMessages((current) => current.map((m) => (m.id === messageId ? { ...m, feedback: value } : m)));
    },
    [messages],
  );

  return {
    conversationId,
    messages,
    status,
    error,
    send,
    stop,
    reset,
    rate,
    decide,
    expire,
    loadFeed,
    pending,
    attach,
    detach,
    restoring,
    openConversation,
    loadConversations,
    deleteConversations,
    communitySwitched,
  };
}
